"use client"

import { MapPin } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { type AdventureType } from '@prisma/client';
import { api } from '@/trpc/react';
import debounce from 'lodash/debounce';

export const useClickOutside = (ref: React.RefObject<HTMLElement>, handler: (event: MouseEvent | TouchEvent) => void) => {
    useEffect(() => {
        const listener = (event: MouseEvent | TouchEvent) => {
            if (!ref.current || ref.current.contains(event.target as Node)) {
                return;
            }
            handler(event);
        };

        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);

        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref, handler]);
};

// Memoize this hook to prevent unnecessary re-renders
export const useTypingAnimation = (suggestions: string[], displayDuration: number, idleDuration: number) => {
    const [index, setIndex] = useState(0);
    const initialWord = suggestions[0] ?? "";
    const [subIndex, setSubIndex] = useState(initialWord.length);
    const [word, setWord] = useState(initialWord);
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        // No need to run animation if component is not visible or focused
        if (document.hidden) return undefined;
        
        if (suggestions[index] && subIndex < suggestions[index].length) {
            const timeout = setTimeout(() => {
                setWord((prevWord) => prevWord + (suggestions[index]?.[subIndex] ?? ''));
                setSubIndex((prevSubIndex) => prevSubIndex + 1);
            }, 150);
            return () => clearTimeout(timeout);
        } else if (word.length > 0 && isVisible) {
            const timeout = setTimeout(() => {
                setIsVisible(false);
            }, displayDuration);
            return () => clearTimeout(timeout);
        } else if (!isVisible && word.length > 0) {
            const timeout = setTimeout(() => {
                setWord((prevWord) => prevWord.slice(0, -1));
            }, 100);
            return () => clearTimeout(timeout);
        } else {
            const timeout = setTimeout(() => {
                setSubIndex(0);
                setWord('');
                setIsVisible(true);
                setIndex((prevIndex) => (prevIndex + 1) % suggestions.length);
            }, idleDuration);
            return () => clearTimeout(timeout);
        }
    }, [subIndex, index, word, isVisible, suggestions, displayDuration, idleDuration]);

    return word;
};

const TripSearchBar = ({ className, btnName }: { className?: string; btnName?: string }) => {
    const [open, setOpen] = useState<boolean>(false);
    const [input, setInput] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Lazy initialize the mutation - only when needed
    const { mutateAsync, data } = api.trips.searchTripsTreks.useMutation({
        onMutate: () => setIsSearching(true),
        onSettled: () => setIsSearching(false)
    });

    const suggestions = useMemo(() => [
        'Malvan', 'Hampi', 'Gokarna', 'Goa', 'Pondicherry', 'Manali', 'Kedarnath', 'Rishikesh', 'Murudeshwar',
        'Uttarakhand', 'Kerala', 'Tamilnadu', 'Sahyadri Treks', 'Dwarka Somnath', 'Rann of Kutch', 'Ujjain Omkareshwar',
        'Spiti Valley', 'Ladakh', 'Rajasthan'
    ], []);

    const word = useTypingAnimation(suggestions, 2000, 100);

    // Reference for search container to detect outside click
    const ref = useRef<HTMLDivElement>(null);

    useClickOutside(ref, () => setOpen(false)); // Hide results on outside click

    // More aggressive debounce to reduce unnecessary API calls
    const debouncedSearch = useMemo(() => debounce(async (query: string) => {
        if (query.trim().length < 2) return; // Only search for 2+ characters
        await mutateAsync({ querykey: query });
    }, 400), [mutateAsync]);

    // Auto-focus input when search bar is opened
    useEffect(() => {
        if (inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, []);

    // Handle input change
    const handleChange = useCallback((e: { target: { value: string; }; }) => {
        const value = e.target.value;
        setInput(value);
        
        if (value.trim().length >= 2) {
            setOpen(true);
            void debouncedSearch(value);
        } else {
            setOpen(false);
        }
    }, [debouncedSearch]);

    const onDestinationSelect = useCallback((slug: string, adentureType: AdventureType) => {
        const path = adentureType === 'Trip' ? '/trip/' : '/trek/';
        const fullUrl = window.location.origin + path + slug;
        setOpen(false);
        setInput('');
        window.open(fullUrl, "_blank");
    }, []);

    // Cancel pending searches when component unmounts
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    return (
        <div ref={ref} className="relative h-fit w-full max-w-md flex justify-center">
            <div
                className={cn(
                    'flex flex-row justify-between items-center gap-8 mt-6 bg-[#1E1E1E]/10 backdrop-blur-sm w-full lg:max-w-xl h-fit rounded-lg lg:p-2 lg:pl-3 p-2 text-sm lg:text-base border border-white/40 text-white transition-colors',
                    className
                )}
            >
                <div className="flex flex-row gap-2 items-center h-fit w-full">
                    <MapPin className="h-5 w-5 opacity-80 -mt-0.5 shrink-0" strokeWidth={1.8} />
                    <input
                        ref={inputRef}
                        placeholder={`Search for ${word}`}
                        className="bg-transparent focus-visible:outline-none placeholder:text-zinc-300 w-full pb-1 font-normal"
                        value={input}
                        onChange={handleChange}
                        onClick={() => input.length >= 2 && setOpen(true)} // Only show results if we have enough input
                    />
                </div>
                <button 
                    className="hidden md:block primary-btn bg-custom-primary shrink-0 px-4 py-2 rounded-md hover:bg-custom-primary/90 transition-colors"
                    onClick={() => {
                        if (input.trim()) {
                            void debouncedSearch(input);
                            setOpen(true);
                        }
                    }}
                >
                    {btnName ?? 'Explore Trip'}
                </button>
            </div>

            {open && input.length >= 2 && (
                <div className="h-64 w-full max-w-xl bg-white rounded-lg absolute z-10 top-full left-1/2 -translate-x-1/2 shadow-[0px_4px_24px_0px_rgba(0,0,0,0.1)] mt-2 flex flex-col gap-2 overflow-y-auto hide-vertical-scrollbar py-2 px-4">
                    {isSearching ? (
                        <div className="flex items-center justify-center h-full w-full">
                            <div className="animate-pulse flex flex-col gap-2 w-full">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <div key={i} className="h-10 bg-gray-200 rounded-md w-full" />
                                ))}
                            </div>
                        </div>
                    ) : data && data.length > 0 ? (
                        data.map((result) => (
                            <Button 
                                key={result.slug} 
                                onClick={() => onDestinationSelect(result.slug, result.adentureType)} 
                                variant="outline" 
                                className='border-custom-primary-dark border-2 hover:bg-custom-primary/30 justify-start text-wrap text-start'
                            >
                                <MapPin className="h-4 w-4 mr-2" />
                                {result.title}
                            </Button>
                        ))
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <p className="text-gray-500">No results found for &quot;{input}&quot;</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TripSearchBar;